import { property } from "lit/decorators.js";
import {
  Color,
  NormalBlending,
  OneMinusSrcAlphaFactor,
  SrcAlphaFactor,
  Vector2,
} from "three";
import { customElement } from "lit/decorators.js";
import { OutlinePass } from "three/examples/jsm/postprocessing/OutlinePass.js";
import { Engine, Entity } from "../../../engine/Engine";
import { SugarElement } from "../../viewport/SugarCanvas";
import { OutlineTslEffect } from "./tsl/TslEffects";

/** OutlinePass blur kernel max is 4; keep a thin visible edge. */
const DEFAULT_EDGE_THICKNESS = 1.5;
/** Alpha saturates quickly with opaque overlay; keep modest. */
const DEFAULT_EDGE_STRENGTH = 4;
/** Soft outer ring from the quarter-res blur buffer. */
const DEFAULT_EDGE_GLOW = 0.2;
/** Peak edge alpha — translucent teal, still readable on empty BG. */
const DEFAULT_EDGE_OPACITY = 0.45;
/** Original selection teal. */
const DEFAULT_EDGE_COLOR = 0x009587;

/**
 * Force every selected-mask pixel onto the visible-edge path so outline does
 * not depend on occlusion against non-selected depth (x-ray silhouette).
 */
function forceOutlineAlwaysVisible(pass: OutlinePass): void {
  const material = pass.prepareMaskMaterial;
  const patched = material.fragmentShader.replace(
    /float depthTest = \(-vPosition\.z > viewZ\) \? 1\.0 : 0\.0;/,
    "float depthTest = 1.0;",
  );
  if (patched === material.fragmentShader) return;
  material.fragmentShader = patched;
  material.needsUpdate = true;
}

/**
 * Stock OutlinePass uses AdditiveBlending (`dst + edge`). On empty / bright
 * backgrounds that washes the edge out — it only reads clearly over dark
 * geometry behind the selection. Switch to alpha-over with a solid edge.
 */
function configureOpaqueOutlineOverlay(pass: OutlinePass): void {
  const material = pass.overlayMaterial;
  material.blending = NormalBlending;
  material.blendSrc = SrcAlphaFactor;
  material.blendDst = OneMinusSrcAlphaFactor;
  material.transparent = true;
  material.depthTest = false;
  material.depthWrite = false;

  const next = material.fragmentShader.replace(
    /vec4 finalColor = edgeStrength \* maskColor\.r \* edgeValue;\s*if\(usePatternTexture\)\s*finalColor \+= \+ visibilityFactor \* \(1\.0 - maskColor\.r\) \* \(1\.0 - patternColor\.r\);\s*gl_FragColor = finalColor;/,
    `float edge = clamp(length(edgeValue.rgb) * edgeStrength, 0.0, 1.0);
					vec3 rgb = edge > 1e-4 ? normalize(edgeValue.rgb) : vec3(0.0);
					if (usePatternTexture) {
						edge = max(edge, visibilityFactor * (1.0 - maskColor.r) * (1.0 - patternColor.r));
					}
					gl_FragColor = vec4(rgb, edge * ${DEFAULT_EDGE_OPACITY.toFixed(2)});`,
  );
  if (next !== material.fragmentShader) {
    material.fragmentShader = next;
    material.needsUpdate = true;
  }
}

class OutlinePostProcessEntity extends Entity {
  private _outlinePass?: OutlinePass;
  private _tslEffect?: OutlineTslEffect;
  edgeThickness = DEFAULT_EDGE_THICKNESS;
  edgeStrength = DEFAULT_EDGE_STRENGTH;

  private readonly _updateCameraBound = () => {
    if (!this._outlinePass || !this.engine?.activeCamera) return;
    this._outlinePass.renderCamera = this.engine.activeCamera.instance;
  };

  onAdd(engine: Engine): void {
    const tryAdd = () => {
      if (!engine.activeCamera || !engine.activeScene) {
        requestAnimationFrame(tryAdd);
        return;
      }
      this.initOutline();
    };
    tryAdd();
  }

  onRemove(engine: Engine): void {
    if (this._tslEffect) {
      this._tslEffect.setSelectedObjects([]);
      engine.removeEffect(this._tslEffect);
      this._tslEffect = undefined;
    }
    if (this._outlinePass) {
      engine.removePass(this._outlinePass);
      engine.removeEventListener("update-camera", this._updateCameraBound);
      this._outlinePass = undefined;
    }
  }

  applyEdgeStyle(thickness: number, strength: number): void {
    this.edgeThickness = thickness;
    this.edgeStrength = strength;
    this._tslEffect?.setEdgeStyle(thickness, strength);
    if (this._outlinePass) {
      this._outlinePass.edgeThickness = thickness;
      this._outlinePass.edgeStrength = strength;
    }
  }

  private initOutline() {
    if (!this.engine?.activeScene || !this.engine.activeCamera) return;

    if (this.engine.renderer.isWebGPURenderer) {
      if (this._tslEffect) return;
      this._tslEffect = new OutlineTslEffect({
        edgeThickness: this.edgeThickness,
        edgeStrength: this.edgeStrength,
        edgeColor: DEFAULT_EDGE_COLOR,
      });
      this.engine.addEffect(this._tslEffect);
      this.engine.dispatchCustomEvent("request-update");
      return;
    }

    if (this._outlinePass) return;

    this._outlinePass = new OutlinePass(
      new Vector2(512, 512),
      this.engine.activeScene,
      this.engine.activeCamera.instance,
    );
    this._outlinePass.enabled = true;
    this._outlinePass.edgeThickness = this.edgeThickness;
    this._outlinePass.edgeStrength = this.edgeStrength;
    this._outlinePass.edgeGlow = DEFAULT_EDGE_GLOW;
    this._outlinePass.visibleEdgeColor = new Color(DEFAULT_EDGE_COLOR);
    this._outlinePass.hiddenEdgeColor = new Color(DEFAULT_EDGE_COLOR);
    this._outlinePass.pulsePeriod = 0;
    forceOutlineAlwaysVisible(this._outlinePass);
    configureOpaqueOutlineOverlay(this._outlinePass);

    this.engine.addPass(this._outlinePass);
    this.engine.addEventListener("update-camera", this._updateCameraBound);
  }
}

@customElement("outline-postprocess")
export class OutlinePostProcessElement extends SugarElement {
  @property({ type: Number, attribute: "edge-thickness" })
  edgeThickness = DEFAULT_EDGE_THICKNESS;

  @property({ type: Number, attribute: "edge-strength" })
  edgeStrength = DEFAULT_EDGE_STRENGTH;

  createEntity(): Entity | undefined {
    const entity = new OutlinePostProcessEntity();
    entity.edgeThickness = this.edgeThickness;
    entity.edgeStrength = this.edgeStrength;
    return entity;
  }

  updated(changed: Map<string, unknown>): void {
    super.updated?.(changed as never);
    const ent = this.entity as OutlinePostProcessEntity | undefined;
    if (
      ent &&
      (changed.has("edgeThickness") || changed.has("edgeStrength"))
    ) {
      ent.applyEdgeStyle(this.edgeThickness, this.edgeStrength);
    }
  }
}
